/**
 * 파일 다운로드 유틸리티
 *
 * 오디오 파일, 이미지, 비디오 등을 다운로드하는 헬퍼 함수를 제공합니다.
 * CORS 제한을 우회하고 적절한 파일명으로 다운로드합니다.
 *
 * @module lib/utils/download
 */

import { logger } from './logger';

/**
 * URL에서 파일명 추출
 */
function extractFilename(url: string, fallback: string = 'download'): string {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const filename = pathname.split('/').pop();
    if (filename && filename.includes('.')) {
      return filename;
    }
  } catch {
    // URL 파싱 실패 시 fallback 사용
  }
  return fallback;
}

/**
 * 파일명 sanitize (특수문자 제거)
 */
function sanitizeFilename(filename: string): string {
  return filename
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, '_')
    .substring(0, 200); // 파일명 길이 제한
}

/**
 * 직접 URL에서 파일 다운로드 (Blob 방식)
 *
 * 오디오, 이미지 등의 파일을 다운로드합니다.
 * CORS가 허용된 경우에만 작동합니다.
 *
 * @param url - 다운로드할 파일의 URL
 * @param filename - 저장할 파일명 (선택사항)
 * @returns Promise<boolean> - 다운로드 성공 여부
 */
export async function downloadFile(
  url: string,
  filename?: string
): Promise<boolean> {
  try {
    // URL에서 파일명 추출 또는 제공된 파일명 사용
    const finalFilename = filename
      ? sanitizeFilename(filename)
      : sanitizeFilename(extractFilename(url, 'audio.mp3'));

    // Fetch로 파일 다운로드
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Blob으로 변환
    const blob = await response.blob();

    // Blob URL 생성
    const blobUrl = window.URL.createObjectURL(blob);

    // 다운로드 링크 생성 및 클릭
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = finalFilename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    // 정리
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);
    }, 100);

    return true;
  } catch (error) {
    logger.error('파일 다운로드 실패:', error);
    // Fallback: 새 탭에서 열기
    return downloadWithNewTab(url, filename);
  }
}

/**
 * 새 탭에서 파일 열기 (Fallback)
 *
 * CORS 문제로 Blob 다운로드가 실패할 경우 사용합니다.
 * 브라우저가 파일을 직접 처리하도록 합니다.
 */
export function downloadWithNewTab(
  url: string,
  _filename?: string
): boolean {
  try {
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  } catch (error) {
    logger.error('새 탭 열기 실패:', error);
    return false;
  }
}

/**
 * 오디오 파일 다운로드 헬퍼
 *
 * 트랙 버전의 오디오 파일을 다운로드합니다.
 *
 * @param audioUrl - 오디오 파일 URL
 * @param trackTitle - 트랙 제목 (파일명에 사용)
 * @param versionNumber - 버전 번호 (파일명에 사용)
 */
export async function downloadAudio(
  audioUrl: string,
  trackTitle?: string,
  versionNumber?: number
): Promise<boolean> {
  // 파일 확장자 추출
  const ext = audioUrl.split('.').pop()?.split('?')[0] || 'mp3';

  // 파일명 생성
  let filename: string;
  if (trackTitle) {
    const sanitizedTitle = sanitizeFilename(trackTitle);
    filename = versionNumber
      ? `${sanitizedTitle}_v${versionNumber}.${ext}`
      : `${sanitizedTitle}.${ext}`;
  } else {
    filename = extractFilename(audioUrl, `audio.${ext}`);
  }

  return downloadFile(audioUrl, filename);
}

/**
 * 다운로드 결과 타입
 */
interface DownloadResult {
  success: number;
  failed: number;
}

/**
 * 여러 파일 일괄 다운로드
 *
 * 지정된 딜레이를 두고 순차적으로 다운로드합니다.
 *
 * @param items - 다운로드할 파일 목록 (url, filename)
 * @param delayMs - 각 다운로드 사이의 딜레이 (ms)
 * @returns Promise<DownloadResult> - 성공/실패 개수
 */
export async function downloadMultiple(
  items: Array<{ url: string; filename?: string }>,
  delayMs: number = 500
): Promise<DownloadResult> {
  const result: DownloadResult = { success: 0, failed: 0 };

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    // 딜레이 적용 (첫 번째 항목 제외)
    if (i > 0 && delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    try {
      const success = await downloadFile(item.url, item.filename);
      if (success) {
        result.success++;
      } else {
        result.failed++;
      }
    } catch {
      result.failed++;
    }
  }

  return result;
}

/**
 * 다운로드 가능한 URL인지 확인
 *
 * URL이 존재하고 유효한 형식인지 확인합니다.
 *
 * @param url - 확인할 URL (null/undefined 가능)
 * @returns boolean - 다운로드 가능 여부
 */
export function isDownloadableUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmedUrl = url.trim();
  if (trimmedUrl === '') {
    return false;
  }

  // 유효한 URL 형식인지 확인
  try {
    new URL(trimmedUrl);
    return true;
  } catch {
    // 상대 경로도 허용
    return trimmedUrl.startsWith('/') || trimmedUrl.startsWith('./');
  }
}

